import React, { useState, useCallback, useEffect } from 'react';
import type { BlockComponentProps } from 'client-react-block-renderer/src';
import axios from 'axios';
import { ucFirst } from './lib/ucFirst';
import RegistrationForm from './RegistrationForm';
import { getCountryList } from './getCountryList';
import getAppState from 'app-state/src/getAppState';
import { useCaptchaActionEnabled } from '@rcm/recaptcha-2';
import { EVENT_REGISTRATION } from '@rcm/event-registration';
interface Props extends BlockComponentProps<BlockData, BlockConfig> {}

interface BlockConfig {
  eventId: string;
}

interface BlockData {}

const RegistrationFormContainer = (props: Props) => {
  const defaultFormData = {
    // @ts-ignore
    countryIso3: getAppState('site').countryIso3,
    email: '',
    firstName: '',
    lastName: '',
  };
  const [formData, setFormData] = useState(defaultFormData);
  const [displayState, setDisplayState] = useState('loading');
  const [captcha, setCaptcha] = useState('');
  const countryList = getCountryList();

  const isCaptchaEnabled = useCaptchaActionEnabled(EVENT_REGISTRATION);

  const handleFormInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFormData = { ...formData };
      let newValue = e.target.value;
      if (e.target.name !== 'email') {
        newValue = ucFirst(newValue);
      }
      newFormData[e.target.name] = newValue;
      setFormData(newFormData);
    },
    [formData]
  );

  const handleCaptchaChange = useCallback((newValue: string) => {
    setCaptcha(newValue);
  }, []);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (formData.firstName.length === 0 || formData.lastName.length === 0 || formData.email.length === 0) {
        setDisplayState('errorFormDataIsInvalid');
        return;
      }
      setDisplayState('loading');
      axios
        .post('/api/v2/free-simple-event-registration/public/tickets', {
          ...formData,
          eventId: parseInt(props.config.eventId),
          ...(isCaptchaEnabled ? { captcha } : {}),
        })
        .then(() => {
          setDisplayState('registeredSuccessFully');
        })
        .catch((error) => {
          if (error.response && error.response.status && error.response.status === 422) {
            setDisplayState('errorFormDataIsInvalid');
          } else {
            setDisplayState('errorCommunicatingWithServer');
          }
        });
    },
    [formData, props.config.eventId, isCaptchaEnabled, captcha]
  );

  const handleRegisterAnotherClick = useCallback(() => {
    setFormData(defaultFormData);
    setDisplayState('formReady');
  }, []);

  useEffect(() => {
    if (props.config.eventId.length === 0) {
      setDisplayState('errorMissingEventId');
      return;
    }
    setDisplayState('formReady');
  }, []);

  return (
    <RegistrationForm
      displayState={displayState}
      formData={formData}
      countryList={countryList}
      handleFormInputChange={handleFormInputChange}
      handleCaptchaChange={handleCaptchaChange}
      handleFormSubmit={handleFormSubmit}
      handleRegisterAnotherClick={handleRegisterAnotherClick}
    />
  );
};

export default RegistrationFormContainer;
